import { Font } from '@react-pdf/renderer';
import { brandIsNetwork } from './plans';
import OswaldRegular from '../assets/fonts/Oswald-Regular.ttf';
import OswaldMedium from '../assets/fonts/Oswald-Medium.ttf';
import OswaldBold from '../assets/fonts/Oswald-Bold.ttf';
import LatoRegular from '../assets/fonts/Lato-Regular.ttf';
import LatoBold from '../assets/fonts/Lato-Bold.ttf';
import LatoItalic from '../assets/fonts/Lato-Italic.ttf';

export const isAdmin = ( role ) => {
    return [ 'ADMIN' ].includes( role );
}

export const isAM = ( role ) => {
    return [ 'ACCOUNT_MANAGER_ADMIN', 'ADMIN' ].includes( role );
}

export const isBrand = ( role ) => {
    // TDC-1499 parity: 'brand sales' is a full brand-portal seat — same
    // surfaces and copy as admin/member. Use isBrandSales for the few spots
    // that stay member-only (SKU management, API keys).
    return [ 'BRAND_ADMIN', 'BRAND_MEMBER', 'BRAND_TEAM_SALES' ].includes( role );
}

export const isBrandSales = ( role ) => {
    return [ 'BRAND_TEAM_SALES' ].includes( role );
}

export const isRMN = ( user ) => {
    // A user is a network user if ANY of their brands is a network root
    // (plan enterprise_rmn | enterprise_agency — the API derives rmn from
    // it; utils/plans.brandIsNetwork reads either). Reading brand[0] only
    // was non-deterministic for multi-brand users (the brand array has no
    // guaranteed order). Always returns a boolean.
    return user?.brand?.some( ( b ) => brandIsNetwork( b ) ) ?? false;
}

// TEMP (TDC-1414): estimated (à-la-carte) pricing is hidden from every
// BRAND_* role (and RMN users) while package pricing is reworked. Package
// terms and locked "actual" prices still show. Remove this and its call
// sites to restore estimates for brands.
export const hideEstimatedPricing = ( role, user ) => {
    return isBrand( role ) || isBrandSales( role ) || isRMN( user );
}

// An order / project / proposal belongs to an RMN deal when its brand is
// rmn:true, any of its brands[] is, it carries an attached RMN network
// (orders: rmn_brand, resolved through brand_projects_brands — the order's
// own brand may be a supplier), or its proposal_meta is stamped with the
// network (proposals whose brand_id resolved to a supplier under the RMN
// lens).
export const isRmnEntity = ( entity ) => {
    return !!(
        entity?.brand?.rmn ||
        entity?.rmn_brand ||
        entity?.brands?.some( ( b ) => b?.rmn ) ||
        entity?.proposal_meta?.rmn_brand_id
    );
}

// TDC-1591: pricing is hidden when the thing being priced belongs to an RMN
// deal — for EVERY role, internal staff included — or, when the entity
// can't tell us, when the viewer is an RMN user. Not even package terms,
// locked "actual" prices, or the "$20k" anonymous price teaser show.
// Internal roles keep pricing only on non-RMN work.
export const hideAllPricing = ( role, user, entity ) => {
    if ( isRmnEntity( entity ) ) return true;
    if ( isInternal( role ) ) return false;
    return isRMN( user );
}

// TDC-1578: the single gate for any surface that shows money on an order,
// project or proposal. Hidden when the priced thing is RMN work (everyone,
// internal included) and hidden from every brand-side viewer — brands see
// no pricing at all for now, not estimates, not locked "actual" prices, not
// package terms. Internal staff keep full pricing on non-RMN work.
export const hidePricing = ( role, user, entity ) => {
    return hideAllPricing( role, user, entity ) || hideEstimatedPricing( role, user );
}
export const isExpert = ( role ) => {
    return [ 'PRO' ].includes( role );
}

export const isProTeam = ( role ) => {
    return [ 'PRO_TEAM_ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'ADMIN' ].includes( role );
}

export const isPostProd = ( role ) => {
    return [ 'POST_PRODUCTION_ADMIN', 'ADMIN' ].includes( role );
}

export const isInternal = ( role ) => {
    return [ 'PRO_TEAM_ADMIN', 'SALES_TEAM', 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'POST_PRODUCTION_ADMIN' ].includes( role );
}

export const resolveProjectBrandId = ( project ) => {
    return (
        project?.brand_id ||
        project?.brands?.find( ( b ) => b.rmn !== true )?.id ||
        project?.brands?.[ 0 ]?.id
    );
}

// Who may download an expert's confirmation details (PDF + video + photos):
// AM, fulfillment, expert team and admin — every internal role but sales.
export const canDownloadConfirmation = ( role ) => {
    return [ 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'POST_PRODUCTION_ADMIN', 'PRO_TEAM_ADMIN' ].includes( role );
}

// Mirrors SOW_COUNTERSIGN_ROLES on the API: who may countersign expert SOWs
// and see countersign state (admins, the expert team and post production).
export const isExpertTeam = ( role ) => {
    return [ 'PRO_TEAM_ADMIN', 'ADMIN', 'POST_PRODUCTION_ADMIN' ].includes( role );
}

export const canEditDeliveryDate = ( role ) => {
    return [ 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'POST_PRODUCTION_ADMIN' ].includes( role );
}

// Mirrors the live/executed values of SOW_STATUS on the API
// (lib/controllers/expertSows.js). 'declined' and 'voided' are surfaced
// separately (internal pills); everything else renders nothing.
export const SOW_ACTIVE_STATUSES = [ 'sent', 'completed' ];

// Mirrors PRICING_EDITOR_USERTYPES on the API: who may change pricing data
// and the content entities managed on the pricing page. All internal roles
// may view.
export const canEditPricing = ( role ) => {
    return [ 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'SALES_TEAM' ].includes( role );
}

// Fulfillment (post production admin) may set a content channel's option
// defaults (e.g. default aspect ratio) but not pricing, channel names, or
// content-type associations. Mirrors CHANNEL_DEFAULTS_EDITOR_USERTYPES on
// the API.
export const canEditChannelDefaults = ( role ) => {
    return canEditPricing( role ) || role === 'POST_PRODUCTION_ADMIN';
}

// Display-only prettifier for machine-ish names: "pdp_video" -> "Pdp Video".
// Never use for values sent back to the API.
export const titleizeName = ( name ) => {
    return String( name || '' )
        .replace( /_/g, ' ' )
        .replace( /\b\w/g, ( c ) => c.toUpperCase() );
}

export const canDeleteProduct = ( role ) => {
    return [ 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'SALES_TEAM' ].includes( role );
}

// Mirrors adminControl on the API (admin / account manager admin / sales):
// the /admin/rmn/* endpoints behind the brand Network tab 401 for any other
// internal role — and a 401 logs the user out via the axios interceptor —
// so the tab and route must not be offered to roles the API will reject.
export const canManageRmnNetwork = ( role ) => {
    return [ 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'SALES_TEAM' ].includes( role );
}

export const productCannotDeleteMessage = ({ projectCount = 0, videoCount = 0 }) => {
    const parts = [];
    if (projectCount > 0) parts.push(`${projectCount} project${projectCount === 1 ? "" : "s"}`);
    if (videoCount > 0) parts.push(`${videoCount} video${videoCount === 1 ? "" : "s"}`);
    const joined = parts.length ? parts.join(" and ") : "a project or video";
    return `This product can not be deleted as it is associated with ${joined}.`;
}

export const canApproveUnapproveVideos = ( role ) => {
    return [ 'PRO_TEAM_ADMIN', 'ADMIN', 'ACCOUNT_MANAGER_ADMIN', 'POST_PRODUCTION_ADMIN', 'BRAND_ADMIN', 'BRAND_MEMBER' ].includes( role );
}

// No brand sales: GET /activity-log deny-lists the role, so the bell would
// only ever 403 for them.
export const shouldShowNotifications = ( role, user ) => {
    return isInternal( role ) || isBrand( role ) || isRMN( user ) || role === 'PRO';
}

export const slugify = ( value, fallback = 'file' ) => {
    return ( value || fallback )
        .toLowerCase()
        .replace( /[^a-z0-9]+/g, '-' )
        .replace( /(^-|-$)/g, '' );
}

export const initialsFromName = ( name ) => {
    return ( name || '' )
        .split( /\s+/ )
        .map( ( part ) => part[0] )
        .filter( Boolean )
        .slice( 0, 2 )
        .join( '' )
        .toUpperCase();
}

export const formatMonthYear = ( iso ) => {
    if ( !iso ) return '';
    return new Date( iso ).toLocaleString( 'en-US', { month: 'long', year: 'numeric' } );
}

let pdfFontsRegistered = false;

export const registerPdfFonts = () => {
    if ( pdfFontsRegistered ) return;
    pdfFontsRegistered = true;
    Font.register( { family: 'Oswald', src: OswaldRegular } );
    Font.register( { family: 'Oswald-Medium', src: OswaldMedium } );
    Font.register( { family: 'Oswald-Bold', src: OswaldBold } );
    Font.register( {
        family: 'Lato',
        fonts: [
            { src: LatoRegular },
            { src: LatoItalic, fontStyle: 'italic' },
        ],
    } );
    Font.register( { family: 'Lato-Bold', src: LatoBold } );
    Font.registerHyphenationCallback( ( word ) => [ word ] );
}

export const formatPageTitle = (title) => {
    if (!title) return title;
    
    return title
        .split(' ')
        .map(word => {
            if (!word) return word;
            
            if (word.length >= 2 && word.length <= 3 && word === word.toUpperCase()) {
                return word.toUpperCase();
            }
            
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
}

export const PRODUCT_FILTER_KEYS = [
    'brand',
    'productCategoryId',
    'proId',
    'videos',
    'hasPdp',
    'cv',
];

export const VIDEO_FILTER_KEYS = [
    'proId',
    'productId',
    'brandId',
    'aspectRatio',
    'extraFilter',
    'contentCode',
];

export const VIDEO_V2_FILTER_KEYS = [
    ...VIDEO_FILTER_KEYS,
    'productCategoryId',
    'demo',
    'embed',
    'hasPdp',
    'hasJsonLd',
    'internalStatus',
    'approved',
    'tagId',
];

export const EXPERT_FILTER_KEYS = [
    'brand',
    'profession',
    'subprofession',
    'status',
    'agerange',
    'daterange',
    'ids',
    'headshot',
    'intro_video',
    'tier',
    'gender',
    'ethnicity',
    'is_parent',
    'region',
    'state',
    'recency',
    'expertType',
    'hasFilmed',
];

export const EXPERT_ALWAYS_COUNT_KEYS = ['headshot', 'intro_video', 'is_parent', 'hasFilmed'];

export const getVideoContextExcludeKeys = (pathname = '') => {
    const excludeKeys = [];
    if (pathname.includes('/embeds')) excludeKeys.push('embed');
    if (pathname.includes('/products')) excludeKeys.push('productId', 'brandId');
    if (pathname.includes('/experts')) excludeKeys.push('proId');
    return excludeKeys;
};

export const countActiveFilters = (searchParams, filterKeys, options = {}) => {
    const { alwaysCountKeys = [], excludeKeys = [] } = options;
    let count = 0;
    searchParams.forEach((value, key) => {
        if (excludeKeys.includes(key)) return;
        if (!filterKeys.includes(key)) return;
        if (alwaysCountKeys.includes(key) || (value && value.length > 0)) count += 1;
    });
    return count;
};


export const APP_TOOLTIP_PROPS = {
    tooltip: {
        sx: {
            bgcolor: '#fff',
            color: '#333',
            border: '1px solid #ECECEC',
            borderRadius: '12px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
            p: '10px 12px',
            fontFamily: 'CircularStd',
            fontSize: 12,
            fontWeight: 450,
            maxWidth: 240,
        },
    },
    arrow: { sx: { color: '#fff' } },
};

export const TOOLTIP_EYEBROW_SX = {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#A58450',
    mb: 0.25,
};
